import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { getImageDimensions } from '../../../utils/mediaDetector';

/**
 * LinkedInEmbed - Organism
 * Componente completo para embed de posts do LinkedIn
 * Suporta:
 * - Posts com vídeo (usa embedCode/iframe)
 * - Posts com imagem (mostra a imagem do post)
 * Segue Atomic Design
 */
const LinkedInEmbed = ({ article, isOpen, sx = {} }) => {
  const containerRef = useRef(null);

  // Processar embed quando artigo mudar (apenas para vídeos)
  useEffect(() => {
    if (!article || article.mediaType !== 'LinkedIn' || !article.embedCode) {
      return;
    }

    if (!isOpen) return;

    console.log('💼 Processando embed do LinkedIn (vídeo)...');

    // LinkedIn embeds são iframes, não precisam de script externo
    const timeout = setTimeout(() => {
      if (containerRef.current) {
        const iframe = containerRef.current.querySelector('iframe');
        if (iframe) {
          console.log('✅ Iframe do LinkedIn encontrado');
        }
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [article, isOpen]);

  // Verificar se é LinkedIn
  if (!article || article.mediaType !== 'LinkedIn') return null;

  // Caso 1: LinkedIn com embedCode (vídeo) - renderiza iframe
  if (article.embedCode) {
    // Processar embedCode para remover width/height fixos do iframe
    let processedEmbedCode = article.embedCode;

    // Remover atributos width e height do iframe para torná-lo responsivo
    processedEmbedCode = processedEmbedCode.replace(/width=["']?\d+["']?/gi, '');
    processedEmbedCode = processedEmbedCode.replace(/height=["']?\d+["']?/gi, '');

    // Adicionar style inline para garantir responsividade
    processedEmbedCode = processedEmbedCode.replace(
      /<iframe/gi,
      '<iframe style="width: 100%; min-height: 500px; border: none;"'
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          textAlign: 'center',
          overflow: 'visible',
          lineHeight: 0,
          '& iframe': {
            width: '100% !important',
            maxWidth: { xs: '100%', sm: '100%', md: '504px' },
            height: 'auto !important',
            minHeight: '500px',
            aspectRatio: 'auto',
            borderRadius: '8px',
            border: 'none',
            display: 'block',
            margin: '0 auto'
          },
          ...sx
        }}
        dangerouslySetInnerHTML={{ __html: processedEmbedCode }}
      />
    );
  }

  // Caso 2: LinkedIn SEM embedCode mas COM imagem (post com imagem)
  const imageUrl = article.image || (article.allImages && article.allImages[0]);

  if (imageUrl && imageUrl !== 'Imagem não encontrada') {
    const imageDimensions = getImageDimensions(article);

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...sx
        }}
      >
        <img
          src={imageUrl}
          alt={article.title || 'Post do LinkedIn'}
          style={{
            maxWidth: imageDimensions.maxWidth || '100%',
            width: imageDimensions.width || 'auto',
            height: imageDimensions.height || 'auto',
            display: 'block',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            objectFit: imageDimensions.objectFit || 'contain',
            boxSizing: 'border-box',
            margin: imageDimensions.margin || '0 auto'
          }}
        />
      </Box>
    );
  }

  // Caso 3: LinkedIn sem embed e sem imagem - não renderiza nada
  return null;
};

LinkedInEmbed.propTypes = {
  article: PropTypes.object,
  isOpen: PropTypes.bool,
  sx: PropTypes.object
};

export default LinkedInEmbed;
